import amber from '@mui/material/colors/amber';
import blue from '@mui/material/colors/blue';
import blueGrey from '@mui/material/colors/blueGrey';
import brown from '@mui/material/colors/brown';
import cyan from '@mui/material/colors/cyan';
import deepOrange from '@mui/material/colors/deepOrange';
import deepPurple from '@mui/material/colors/deepPurple';
import green from '@mui/material/colors/green';
import grey from '@mui/material/colors/grey';
import indigo from '@mui/material/colors/indigo';
import lightBlue from '@mui/material/colors/lightBlue';
import lightGreen from '@mui/material/colors/lightGreen';
import lime from '@mui/material/colors/lime';
import orange from '@mui/material/colors/orange';
import pink from '@mui/material/colors/pink';
import purple from '@mui/material/colors/purple';
import red from '@mui/material/colors/red';
import teal from '@mui/material/colors/teal';
import yellow from '@mui/material/colors/yellow';
import diff from 'lodash/difference';
import random from 'lodash/random';

import { theme } from './theme/minerva-main';

const beigeish = ['#E1DABD', '#BDC3A1', '#97AD89', '#6F9776'];
const purpleish = ['#FFE4FA', '#FFD7D4', '#FFD9AD', '#D0CAEB'];
const pinkish = ['#FFC4EB', '#FFC1D9', '#FFC3BB', '#FFE5FF'];
const lightPinkish = ['#F1DEDC', '#D9C0C6', '#BCA4B5', '#978CA7'];
const blueish = ['#CFF6FF', '#A0B3D6', '#A3FFE7', '#A3FFE7', '#AAE8E8'];
const greenish = ['#DFE5CB', '#B1C6B1', '#86A69B', '#90B39A', '#ECF5CE'];
const yellowish = ['#FCFCD4', '#F3F3CC', '#FFEAB3', '#FFDBA2'];

const colors = [
  ...beigeish,
  ...purpleish,
  ...pinkish,
  ...lightPinkish,
  ...blueish,
  ...greenish,
  ...yellowish,
];

const muiColors = [
  red,
  purple,
  indigo,
  blue,
  amber,
  deepOrange,
  deepPurple,
  cyan,
  lightBlue,
  pink,
  teal,
  green,
  lightGreen,
  yellow,
  lime,
  orange,
  brown,
  grey,
  blueGrey,
];

export const createColorGetter = () => {
  let usedColorsCache: Record<string, string> = {};

  return (title: string): string => {
    if (usedColorsCache[title]) {
      return usedColorsCache[title];
    }

    const unusedColors = diff(colors, Object.values(usedColorsCache));
    const newColor = unusedColors[random(0, unusedColors.length - 1)];

    usedColorsCache[title] = newColor;

    return newColor;
  };
};

/**
 * Parses a color string (hex, rgb, rgba) and returns RGB values with optional alpha
 */
const parseColor = (color: string): { r: number; g: number; b: number; a?: number } | null => {
  // Handle hex colors
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
      };
    }
  }

  // Handle rgb/rgba colors
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
      a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
    };
  }

  return null;
};

/**
 * Calculates the relative luminance of a color
 * Based on WCAG 2.0 formula
 * @see https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
 */
const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculates WCAG contrast ratio between two luminance values
 * @see https://webaim.org/articles/contrast/
 */
const getContrastRatio = (l1: number, l2: number): number => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Blends a semi-transparent color with a background (assumed white)
 */
const blendWithWhite = (r: number, g: number, b: number, alpha: number): { r: number; g: number; b: number } => {
  return {
    r: Math.round(r * alpha + 255 * (1 - alpha)),
    g: Math.round(g * alpha + 255 * (1 - alpha)),
    b: Math.round(b * alpha + 255 * (1 - alpha)),
  };
};

/**
 * Returns a contrasting text color based on background color
 * Uses WCAG contrast ratio to choose the best option
 *
 * @param backgroundColor - The background color in hex (#RRGGBB, #RGB) or rgb/rgba format
 * @param lightColor - Color to return for dark backgrounds (default: '#FFFFFF')
 * @param darkColor - Color to return for light backgrounds (default: '#1A1A1A')
 *
 * @see https://webaim.org/articles/contrast/
 * @see https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
 */
export const getContrastTextColor = (
  backgroundColor: string,
  lightColor = '#FFFFFF',
  darkColor = '#1A1A1A',
): string => {
  const parsed = parseColor(backgroundColor);
  if (!parsed) {
    return darkColor;
  }

  // Handle semi-transparent colors by blending with white background
  const rgb = parsed.a !== undefined && parsed.a < 1
    ? blendWithWhite(parsed.r, parsed.g, parsed.b, parsed.a)
    : parsed;

  const bgLuminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // Calculate contrast ratio with both options
  const lightParsed = parseColor(lightColor);
  const darkParsed = parseColor(darkColor);

  const lightLuminance = lightParsed ? getLuminance(lightParsed.r, lightParsed.g, lightParsed.b) : 1;
  const darkLuminance = darkParsed ? getLuminance(darkParsed.r, darkParsed.g, darkParsed.b) : 0;

  const contrastWithLight = getContrastRatio(bgLuminance, lightLuminance);
  const contrastWithDark = getContrastRatio(bgLuminance, darkLuminance);

  // Choose the color with better contrast ratio
  return contrastWithLight > contrastWithDark ? lightColor : darkColor;
};

export const percentToHex = (value: number) => {
  const percent = Math.max(0, Math.min(100, value)); // bound percent from 0 to 100
  const intValue = Math.round((percent / 100) * 255); // map percent to nearest integer (0 - 255)
  const hexValue = intValue.toString(16); // get hexadecimal representation
  return hexValue.padStart(2, '0').toUpperCase(); // format with leading 0 and upper case characters
};

export const generateColoredBoxItem = (id: number) => {
  return {
    color: muiColors[id][600],
    borderColor: muiColors[id][600],
    backgroundColor: muiColors[id][50],
  };
};

export const generateSystemTypeColors = (id: number, name: string) => {
  const getColor = createColorGetter();
  const colorTypeMap = [
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.primary.light,
    theme.palette.minervaColors.primaryViolet,
    theme.palette.error.main,
    theme.palette.minervaColors.primaryRed,
  ];
  const bgColorTypeMap = [
    theme.palette.minervaColors.secondaryOrange,
    theme.palette.minervaColors.secondaryGreen,
    theme.palette.minervaColors.secondaryBlue,
    theme.palette.minervaColors.secondaryViolet,
    theme.palette.minervaColors.secondaryOrange,
    theme.palette.minervaColors.secondaryRed,
  ];

  const totalColors = colorTypeMap.length;
  return {
    color: id <= totalColors ? colorTypeMap[id - 1] : getColor(name),
    borderColor: id <= totalColors ? colorTypeMap[id - 1] : getColor(name),
    backgroundColor:
      id <= totalColors ? bgColorTypeMap[id - 1] : getColor(`${name}1`),
  };
};
const activityTypesColorMap = [
  {
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
    backgroundColor: theme.palette.minervaColors.secondaryOrange,
  },
  {
    color: 'rgb(57, 73, 127)',
    borderColor: 'rgb(170, 185, 236)',
    backgroundColor: 'rgb(147, 169, 241, 0.5)',
  },
  {
    color: 'rgb(128,78,72)',
    borderColor: 'rgb(199,131,122)',
    backgroundColor: 'rgb(235, 179, 171, 0.5)',
  },
  {
    color: 'rgb(40, 109, 117)',
    borderColor: 'rgb(101, 159, 223)',
    backgroundColor: 'rgb(215, 240, 247)',
  },
  {
    color: 'rgb(119, 76, 128)',
    borderColor: 'rgb(172,134,180)',
    backgroundColor: 'rgb(240, 211, 248, 0.5)',
  },
  {
    color: theme.palette.minervaColors.primaryRed,
    borderColor: 'rgb(201,108,53)',
    backgroundColor: theme.palette.minervaColors.secondaryRed,
  },
  {
    color: 'rgb(64,136,69)',
    borderColor: 'rgb(64,136,69)',
    backgroundColor: 'rgb(112, 187, 117, 0.5)',
  },
  {
    color: 'rgb(110,82,157)',
    borderColor: 'rgb(110,82,157)',
    backgroundColor: 'rgb(179, 148, 223, 0.5)',
  },
  {
    color: 'rgb(75,190,196)',
    borderColor: 'rgb(75,190,196)',
    backgroundColor: 'rgb(135, 247, 253, 0.5)',
  },
];

export const generateActivityTypeColors = (id: number, name: string) => {
  const getColor = createColorGetter();

  const typeColor = activityTypesColorMap[id - 1] ?? {
    color: getColor(name),
    borderColor: getColor(name),
    backgroundColor: getColor(`${name}1`),
  };
  return typeColor;
};
